import { useContext } from 'react';
import { AppContext } from '@/context/AppContext';
import SummaryStrip from './SummaryStrip';
import CityTabs from './CityTabs';
import KeywordGrid from './KeywordGrid';
import CompetitorChart from './CompetitorChart';
import PageHeader from '@/components/shared/PageHeader';
import PDFExport from '@/components/pdf/PDFExport';
import LoadingSpinner from '@/components/shared/LoadingSpinner';
import EmptyState from '@/components/shared/EmptyState';
import { Play, Download } from 'lucide-react';
import { useScan } from '@/hooks/useScan';
import * as api from '@/services/api';

export default function Dashboard() {
  const { 
    activeClient, 
    activeClientId,
    rankings, 
    initialLoading,
    addToast
  } = useContext(AppContext);
  const { isScanning, startScan } = useScan();

  const clientRankings = rankings[activeClientId] || [];

  const handleExportCSV = () => {
    api.exportRankingsCSV(activeClientId, activeClient?.name);
    addToast('CSV export started', 'info');
  };

  if (initialLoading) {
    return (
      <div className="flex items-center justify-center py-32">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!activeClient) {
    return (
      <EmptyState 
        icon="📊" 
        title="No Client Selected" 
        message="Add a client from the Admin panel to get started with SEO tracking." 
      />
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="dashboard">
      {/* Header with actions */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="section-label flex-1">
          <span>Rankings Overview</span>
          <span className="hidden sm:block flex-1 h-px bg-[#1E2D45]" />
        </div>
        <div className="flex items-center justify-center sm:justify-end gap-2 sm:gap-3">
          <button
            onClick={handleExportCSV}
            className="button-ghost text-xs sm:text-sm flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 py-1.5 sm:py-2"
            disabled={clientRankings.length === 0}
            data-testid="export-csv-btn"
          >
            <Download size={14} className="sm:w-4 sm:h-4" />
            <span className="hidden xs:inline">CSV</span>
          </button>
          <PDFExport />
          <button
            onClick={startScan}
            disabled={isScanning}
            className="button-primary text-xs sm:text-sm flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2"
            data-testid="run-scan-btn"
          >
            {isScanning ? (
              <>
                <LoadingSpinner size="sm" />
                Scanning...
              </>
            ) : (
              <>
                <Play size={14} className="sm:w-4 sm:h-4" />
                Run Scan
              </>
            )}
          </button>
        </div>
      </div>

      {/* Summary Strip */}
      <SummaryStrip />

      {/* City Tabs */}
      <CityTabs />

      {/* Keyword Grid */}
      <KeywordGrid />

      {/* Competitor Chart */}
      {clientRankings.length > 0 && <CompetitorChart />}
    </div>
  );
}
